import React, { useContext, useEffect, useState, useRef } from 'react';

import useSocietyProviderService from '../../../app/services/hooks/useSocietyProviderService';
import useBusinessNamesService from '../../../app/services/hooks/useBusinessService';
import useSocietyService from '../../../app/services/hooks/useSocietyService';

import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { toast } from 'react-toastify';
import LazyLoading from '../../../components/LazyLoading';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import { AppContext } from '../../../contexts/AppContext';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { SocietyProviderDataForm, defaultSocietyProviderDataForm } from '../../../app/models/SocietyProvider';
import { BusinessNameDataForm } from '../../../app/models/BusinessName';
import { SocietyDataForm } from '../../../app/models/Society';
import SelectTwo, { SelectTwoMapperOptions } from '../../../components/SelectTwo';
import useHandleErrorFields from '../../../hooks/useHandleErrorFields';

interface Props {
    onSaved?: (societyId?: number) => void;
    onCancel?: () => void;
    onError?: () => void;
}

const ProviderCreate = ({ onSaved, onCancel, onError }: Props) => {
    const { showLoading, hideLoading } = useContext(AppContext);

    const [societies, setSocieties] = useState<SocietyDataForm[]>([]);
    const [selectedSocietyId, setSelectedSocietyId] = useState<number | null>(null);
    const [businessNames, setBusinessNames] = useState<BusinessNameDataForm[]>([]);
    const [selectedBusinessNameId, setSelectedBusinessNameId] = useState<number | null>(null);
    const [selectedBusinessName, setSelectedBusinessName] = useState<BusinessNameDataForm | null>(null);
    const [societyProvider, setSocietyProvider] = useState<SocietyProviderDataForm>(defaultSocietyProviderDataForm);

    const [errorFields, setErrorFields] = useState<ServiceResponse['data'] | undefined>();

    const { 
        fetchingStoreSocietyProvider, 
        storeSocietyProvider, 
        fetchingCreateSocietyProvider, 
        createSocietyProvider 
    } = useSocietyProviderService();

    const { fetchingGetSocieties, getSocieties } = useSocietyService();
    const { editBusinessName } = useBusinessNamesService();

    const isInitialLoad = useRef(true);

    // Cargar sociedades al montar el componente y seleccionar la primera automáticamente
    useEffect(() => {
        if (showLoading) showLoading('loading', 'Cargando sociedades...');

        getSocieties({ page: 1, per_page: 100, sort: 'id', order: 'asc', rut: '' }, {
            onSuccess: (response: ServiceResponse) => {
                const societiesList = response.data.societies || [];
                setSocieties(societiesList);
                
                // Seleccionar automáticamente la primera sociedad
                if (societiesList.length > 0 && !selectedSocietyId) {
                    const firstSocietyId = societiesList[0].id;
                    if (firstSocietyId) {
                        // No ocultar loading aquí, se ocultará después de cargar las razones sociales
                        setSelectedSocietyId(firstSocietyId);
                    } else {
                        if (hideLoading) hideLoading();
                    }
                } else {
                    if (hideLoading) hideLoading();
                }
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                toast.error(response.message);
                if (onError) onError();
            }
        });
    }, []);

    // Cargar razones sociales cuando se selecciona una sociedad
    useEffect(() => {
        if (selectedSocietyId) {
            // Solo mostrar loading si no es la carga inicial (para evitar parpadeo)
            if (!isInitialLoad.current && showLoading) {
                showLoading('loading', 'Cargando razones sociales...');
            }

            createSocietyProvider(selectedSocietyId, {
                onSuccess: (response: ServiceResponse) => {
                    setBusinessNames(response.data.business_names || []);
                    setSocietyProvider({
                        ...defaultSocietyProviderDataForm,
                        society_id: selectedSocietyId
                    });
                    // Limpiar selección de razón social cuando cambia la sociedad
                    setSelectedBusinessNameId(null);
                    setSelectedBusinessName(null);
                    
                    // Marcar que la carga inicial terminó
                    if (isInitialLoad.current) {
                        isInitialLoad.current = false;
                    }
                    
                    if (hideLoading) hideLoading();
                },
                onError: (response: ServiceResponse) => {
                    if (hideLoading) hideLoading();
                    toast.error(response.message);
                    if (isInitialLoad.current) {
                        isInitialLoad.current = false;
                    }
                }
            });
        } else {
            // Limpiar datos cuando no hay sociedad seleccionada
            setBusinessNames([]);
            setSelectedBusinessNameId(null);
            setSelectedBusinessName(null);
        }
    }, [selectedSocietyId]);

    const handleBusinessNameChange = (businessNameId: number | null) => {
        setSelectedBusinessNameId(businessNameId);
        setSelectedBusinessName(null);
        setErrorFields(undefined);

        if (businessNameId) {
            if (showLoading) showLoading('loading', 'Cargando datos de la razón social...');
            editBusinessName(businessNameId, {
                onSuccess: (response: ServiceResponse) => {
                    const businessName = response.data.business_name;
                    setSelectedBusinessName(businessName);
                    // Pre-llenar datos del proveedor desde la razón social
                    setSocietyProvider({
                        ...societyProvider,
                        business_name_id: businessNameId
                    });
                    if (hideLoading) hideLoading();
                },
                onError: (response: ServiceResponse) => {
                    if (hideLoading) hideLoading();
                    toast.error(response.message);
                }
            });
        }
    };

    const store = () => {
        if (!selectedSocietyId) {
            toast.warning('Debe seleccionar una sociedad.');
            return;
        }

        if (!selectedBusinessNameId) {
            toast.warning('Debe seleccionar un proveedor.');
            return;
        }

        if (showLoading) showLoading('loading', 'Guardando proveedor...');
        
        const dataToStore: SocietyProviderDataForm = {
            society_id: selectedSocietyId,
            business_name_id: selectedBusinessNameId
        };

        storeSocietyProvider(dataToStore, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });
                toast.success(response.message, {
                    autoClose: 2500
                });
                // Pasar el society_id al callback para actualizar el filtro
                if (onSaved) onSaved(selectedSocietyId || undefined);
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            },
            onFieldError: (errorFields: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setErrorFields(errorFields.data);
            }
        });
    };

    const cancel = () => {
        if (onCancel) onCancel();
    };

    const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors } = useHandleErrorFields();

    useEffect(() => {
        setErrors(errorFields);
    }, [errorFields]);

    const handleSocietyChange = (societyId: number | null) => {
        setSelectedSocietyId(societyId);
        setErrorFields(undefined);
    };

    return fetchingGetSocieties || fetchingCreateSocietyProvider ? (
        <LazyLoading height={300} />
    ) : (
        <>
            <div className="row">
                <div className="col-12">
                    <div className="mb-3 row">
                        <div className="col-md-12">
                            <label className="col-form-label">Sociedad</label>
                            <SelectTwo
                                name="society_id"
                                inputValue={selectedSocietyId}
                                options={SelectTwoMapperOptions(societies, 'id', ['rut', 'business_name'])}
                                onChange={(option: { value: number | null } | null) => handleSocietyChange(option?.value || null)}
                                placeholder="Seleccione una sociedad"
                                hasError={fieldHasError('society_id') !== ''}
                                onFocus={() => onFocusRemove('society_id')}
                                isClearable={false}
                            />
                            <div className="invalid-feedback" style={{ display: 'flex' }}>
                                {fieldErrorMessage('society_id')}
                            </div>
                        </div>
                    </div>

                    {selectedSocietyId && (
                        <div className="mb-3 row">
                            <div className="col-md-12">
                                <label className="col-form-label">Proveedor</label>
                                <SelectTwo
                                    name="business_name_id"
                                    inputValue={selectedBusinessNameId}
                                    options={SelectTwoMapperOptions(businessNames, 'id', ['name'])}
                                    onChange={(option: { value: number | null } | null) => handleBusinessNameChange(option?.value || null)}
                                    placeholder="Seleccione una razón social"
                                    hasError={fieldHasError('business_name_id') !== ''}
                                    onFocus={() => onFocusRemove('business_name_id')}
                                    isClearable={false}
                                />
                                <div className="invalid-feedback" style={{ display: 'flex' }}>
                                    {fieldErrorMessage('business_name_id')}
                                </div>
                            </div>
                        </div>
                    )}

                    {selectedBusinessName && (
                        <>
                            <hr />
                            <h5 className="mb-3">Datos del Proveedor</h5>
                            <div className="mb-2 row">
                                <div className="col-md-6">
                                    <label className="col-form-label">RUT</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={selectedBusinessName.rut || ''}
                                        readOnly
                                        disabled
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="col-form-label">Nombre</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={selectedBusinessName.name || ''}
                                        readOnly
                                        disabled
                                    />
                                </div>
                            </div>
                            <div className="mb-2 row">
                                <div className="col-md-12">
                                    <label className="col-form-label">Dirección</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={selectedBusinessName.address || ''}
                                        readOnly
                                        disabled
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
            <hr />
            <div className="row justify-content-end">
                <div className="col-auto">
                    <ButtonCancelForm callbackCancel={cancel} locked={fetchingStoreSocietyProvider} />
                </div>
                <div className="col-auto">
                    <ButtonSaveForm 
                        callbackSave={store} 
                        locked={fetchingStoreSocietyProvider || !selectedBusinessNameId || !selectedSocietyId} 
                    />
                </div>
            </div>
        </>
    );
};

export default ProviderCreate;
